import React from 'react';

interface NotificationPromptModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  isIOS: boolean;
}

export const NotificationPromptModal: React.FC<NotificationPromptModalProps> = ({
  isOpen,
  onAccept,
  onDecline,
  isIOS,
}) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '20px',
        animation: 'br-fadeIn 0.3s ease-out',
      }}
      onClick={onDecline}
    >
      <div
        style={{
          backgroundColor: isIOS ? 'rgba(255,255,255,0.97)' : '#1a1a2e',
          borderRadius: isIOS ? '28px' : '20px',
          padding: '32px 24px',
          maxWidth: '380px',
          width: '100%',
          boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
          textAlign: 'center',
          animation: 'br-slideUp 0.4s cubic-bezier(0.34,1.56,0.64,1)',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: '56px', marginBottom: '16px', animation: 'br-bell 1.2s ease-in-out infinite' }}>
          🔔
        </div>

        <h2 style={{
          fontSize: isIOS ? '22px' : '20px',
          fontWeight: '700',
          color: isIOS ? '#1f2937' : '#ffffff',
          marginBottom: '10px',
          fontFamily: isIOS ? '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' : 'inherit',
        }}>
          Ativar Notificações?
        </h2>

        <p style={{
          fontSize: '15px',
          color: isIOS ? '#6b7280' : '#94a3b8',
          marginBottom: '20px',
          lineHeight: '1.5',
        }}>
          Receba alertas instantâneos para:
        </p>

        <div style={{
          backgroundColor: isIOS ? 'rgba(0,122,255,0.06)' : 'rgba(255,255,255,0.06)',
          borderRadius: '14px',
          padding: '14px 16px',
          marginBottom: '24px',
          textAlign: 'left',
        }}>
          {[
            { icon: '💬', text: 'Novas mensagens recebidas' },
            { icon: '📞', text: 'Chamadas recebidas' },
            { icon: '📢', text: 'Avisos mesmo com app fechado' },
          ].map(({ icon, text }) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <span style={{ fontSize: '22px' }}>{icon}</span>
              <span style={{ fontSize: '14px', color: isIOS ? '#374151' : '#e2e8f0', fontWeight: '500' }}>{text}</span>
            </div>
          ))}
        </div>

        {isIOS && (
          <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '18px', fontStyle: 'italic' }}>
            🍎 O iOS vai pedir confirmação após você clicar em "Ativar"
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            onClick={onAccept}
            style={{
              width: '100%',
              padding: '15px',
              backgroundColor: isIOS ? '#007AFF' : '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: isIOS ? '14px' : '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
              fontFamily: isIOS ? '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif' : 'inherit',
            }}
          >
            {isIOS ? 'Sim, quero receber!' : 'Ativar Notificações'}
          </button>
          <button
            onClick={onDecline}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: 'transparent',
              color: isIOS ? '#007AFF' : '#64748b',
              border: isIOS ? 'none' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: isIOS ? '14px' : '12px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: isIOS ? '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif' : 'inherit',
            }}
          >
            Agora não
          </button>
        </div>
      </div>

      <style>{`
        @keyframes br-fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes br-slideUp {
          from { opacity: 0; transform: translateY(32px) scale(0.92); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes br-bell {
          0%, 100% { transform: rotate(0deg); }
          10%, 30%  { transform: rotate(-12deg); }
          20%, 40%  { transform: rotate(12deg); }
          50%       { transform: rotate(0deg); }
        }
      `}</style>
    </div>
  );
};

// Modal separado para orientar o usuário iOS a instalar como PWA
interface IOSInstallGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

export const IOSInstallGuide: React.FC<IOSInstallGuideProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '0 0 30px 0',
        animation: 'br-fadeIn 0.3s ease-out',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'rgba(255,255,255,0.97)',
          borderRadius: '24px 24px 24px 24px',
          padding: '28px 24px',
          maxWidth: '400px',
          width: '100%',
          animation: 'br-slideUpBottom 0.4s cubic-bezier(0.34,1.56,0.64,1)',
          position: 'relative',
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Indicador de arraste */}
        <div style={{ width: '40px', height: '4px', backgroundColor: '#d1d5db', borderRadius: '2px', margin: '0 auto 20px' }} />

        <div style={{ fontSize: '48px', textAlign: 'center', marginBottom: '14px' }}>🍎</div>

        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', textAlign: 'center', marginBottom: '8px' }}>
          Instale para receber notificações
        </h2>
        <p style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', marginBottom: '24px', lineHeight: '1.5' }}>
          O iPhone exige que o app seja instalado na tela inicial para enviar notificações push.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
          {[
            {
              step: '1',
              icon: '⬆️',
              title: 'Toque em Compartilhar',
              desc: 'No Safari, toque no ícone de compartilhamento (quadrado com seta) na barra inferior',
            },
            {
              step: '2',
              icon: '📲',
              title: 'Adicionar à Tela de Início',
              desc: 'Role a lista e toque em "Adicionar à Tela de Início"',
            },
            {
              step: '3',
              icon: '✅',
              title: 'Confirme e abra o app',
              desc: 'Toque em "Adicionar" e depois abra o Black Rabbit pela tela inicial',
            },
          ].map(({ step, icon, title, desc }) => (
            <div key={step} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                backgroundColor: '#007AFF',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '700',
                fontSize: '15px',
                flexShrink: 0,
              }}>
                {step}
              </div>
              <div>
                <p style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '3px' }}>
                  {icon} {title}
                </p>
                <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '15px',
            backgroundColor: '#007AFF',
            color: 'white',
            border: 'none',
            borderRadius: '14px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Entendi!
        </button>
      </div>

      <style>{`
        @keyframes br-slideUpBottom {
          from { opacity: 0; transform: translateY(60px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
